import TextInput from '../../shared/TextInput';
import Button from '../../shared/Button';
import Typography, { TypeVariant } from '../../shared/Typography';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';
import Link from 'next/link';
import useValidateEmail from 'hooks/accounts/useValidateEmail';
import useRegister from 'hooks/accounts/useRegister';
import { useState } from 'react';
import { fetchUserProfile } from 'queries/user';
import { useQueryClient } from 'react-query';
import useAuthStore from 'hooks/useAuthStore';
import { PROFILE } from 'constants/routes';
import { useRouter } from 'next/router';

type AccountFields = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const schemaValidation = yup.object({
  firstName: yup.string().min(1).required(),
  lastName: yup.string().min(1).required(),
  email: yup.string().min(1).required(),
  password: yup.string().min(1).required(),
});

const CreateAccountForm = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const initialValues: AccountFields = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  };
  const [error, setError] = useState<string>('');

  const { logUserIn } = useAuthStore();
  const handleValidEmail = useValidateEmail();
  const handleRegister = useRegister();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schemaValidation}
      onSubmit={async (data, { setSubmitting }) => {
        setSubmitting(true);

        try {
          await handleValidEmail.mutateAsync(data.email, {
            onError: () => setError('Email is already in use'),
          });

          handleRegister.mutate(
            {
              data: {
                ...data,
                username: data.email,
                birthday: 'null',
              },
            },
            {
              onError: (err: any) =>
                setError(err?.message || 'Uh oh, we did not see that coming'),
              onSuccess: async () => {
                const user = await fetchUserProfile();
                queryClient.setQueryData('profile', user);
                logUserIn(user.id, user.type);

                // send heap track api call to record custom event for no purchase sign up
                window.heap?.track('Sign Up (No Purhcase)', {
                  id: user.id,
                  email: user.email,
                });

                setSubmitting(false);
                router.push(PROFILE);
              },
            },
          );
        } catch (e) {
          // no actions needed here
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting }) => {
        const buttonDisabled = !!Object.values(values).filter(
          (value) => value.length === 0,
        ).length;

        return (
          <Form>
            <HeaderWrapper>
              <Header>Create Account</Header>
            </HeaderWrapper>
            <SubHeaderWrapper>
              <SubHeader color="var(--color-white)">
                Already have an account?
              </SubHeader>
              <Link href="/login" passHref>
                <LoginAnchor>
                  <SubHeader color="var(--color-primary-500)">Log in</SubHeader>
                </LoginAnchor>
              </Link>
            </SubHeaderWrapper>
            <InputWrapper>
              {!!error ? (
                <SubHeader color="var(--color-secondary-300)">
                  {error}
                </SubHeader>
              ) : null}
              <TextInput
                fullWidth
                label="First Name"
                value={values.firstName}
                type="text"
                onChange={(e) => {
                  setFieldValue('firstName', e);
                }}
                variant="rounded"
                background="light"
              />
              <TextInput
                fullWidth
                label="Last Name"
                value={values.lastName}
                type="text"
                onChange={(e) => {
                  setFieldValue('lastName', e);
                }}
                variant="rounded"
                background="light"
              />
              <TextInput
                fullWidth
                label="Email"
                value={values.email}
                type="text"
                onChange={(e) => {
                  setFieldValue('email', e);
                }}
                variant="rounded"
                background="light"
              />
              <TextInput
                fullWidth
                label="Password"
                value={values.password}
                type="password"
                onChange={(e) => {
                  setFieldValue('password', e);
                }}
                variant="rounded"
                background="light"
              />
              <Button
                fullWidth
                variant="outline"
                size="large"
                type="submit"
                isLoading={isSubmitting}
                disabled={buttonDisabled}
              >
                Create Account
              </Button>
            </InputWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};

type TextProps = {
  children: React.ReactNode;
  color?: string;
};

const HeaderWrapper = styled.div`
  padding-bottom: 14px;
`;

const Header = ({ children }: TextProps) => (
  <Typography
    variant={TypeVariant.header03}
    as="h2"
    phone={TypeVariant.subtitle01}
    color="var(--color-white)"
    textAlign="center"
  >
    {children}
  </Typography>
);

const SubHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const SubHeader = ({ children, color }: TextProps) => (
  <Typography
    variant={TypeVariant.bodyLarge01}
    as="p"
    phone={TypeVariant.body01}
    color={color}
  >
    {children}
  </Typography>
);

const LoginAnchor = styled.a`
  text-decoration: none;
`;

const InputWrapper = styled.div`
  padding-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export default CreateAccountForm;
